/**
 * RoleAssignment Component (Dual-Mode Support)
 *
 * Two-screen flow after Background Survey:
 *
 * Screen 1: Role & Topic Reveal
 *   - Shows assigned role (Teacher/Student/Learner)
 *   - Reveals the topic (except for Students - they see "Learn from Your Partner")
 *   - "Continue" button to proceed
 *   - Note: Example questions are shown later in the LearningPhase sidebar
 *
 * Screen 2: Trait Self-Assessment (Teacher/Learner only)
 *   - Role-specific trait confidence questions (explaining/self-learning ability)
 *   - Submit to proceed to next round
 *   - NOTE: Students SKIP this screen - they answer self-assessment in ExampleQuestions
 *     component just before the Communication Phase (to avoid remembering questions)
 *
 * NOTE: State confidence (3-item scale) is NO LONGER asked here.
 * All confidence questions are asked AFTER learning:
 * - Teacher/Learner: In RoleInstructions (Pre-Chat Preparation / Learning Consolidation)
 * - Student: In PostChatReflection (after Communication Phase)
 *
 * Roles:
 * - DYAD mode:
 *   - Teacher (marker=0): Will learn and teach the topic
 *   - Student (marker>0): Will learn from the Teacher
 * - SINGLE mode:
 *   - Learner: Will learn the topic independently
 */

import React, { useState } from "react";
import { usePlayer, useGame } from "@empirica/core/player/classic/react";
import { ConfidenceScale, isConfidenceComplete, getEmptyConfidenceState } from "./ConfidenceScale";

// Teacher: Explaining Confidence questions (3 questions)
const TEACHER_QUESTIONS = [
  {
    id: "explain_q1",
    question: "Explain complex topics clearly to someone else"
  },
  {
    id: "explain_q2",
    question: "Recognize when your explanation isn't working"
  },
  {
    id: "explain_q3",
    question: "Write a clear explanation of a topic for someone else"
  }
];

// Student: Learning Confidence questions (2 questions)
const STUDENT_QUESTIONS = [
  {
    id: "learn_q1",
    question: "Ask good questions when something is unclear"
  },
  {
    id: "learn_q2",
    question: "Recognize when you don't fully understand something"
  }
];

// Learner (Single mode): Self-directed learning questions (2 questions)
const LEARNER_QUESTIONS = [
  {
    id: "selflearn_q1",
    question: "Learn new topics independently"
  },
  {
    id: "selflearn_q2",
    question: "Identify the most important information while studying"
  }
];

const LIKERT_OPTIONS = [
  { value: "0", label: "0" },
  { value: "1", label: "1" },
  { value: "2", label: "2" },
  { value: "3", label: "3" },
  { value: "4", label: "4" },
  { value: "5", label: "5" },
  { value: "6", label: "6" }
];

export function RoleAssignment({ next, showExamples = true }) {
  const player = usePlayer();
  const game = useGame();

  const marker = player.get("marker");
  const secondaryMarker = player.get("secondary_marker");
  const config = game.get("config");
  const topicName = config?.name || "the topic";
  const exampleQuestions = player.get("exampleQuestions") || [];
  const gameMode = game.get("gameMode") || "dyad";
  const isSingleMode = gameMode === "single";

  // Determine role based on game mode
  const assignedRole = player.get("assignedRole");
  const isLearner = isSingleMode || assignedRole === "Learner";
  const isTeacher = !isSingleMode && marker === 0;
  const isStudent = !isSingleMode && marker > 0;

  // Get display role name
  const roleName = isLearner ? "Learner" : (isTeacher ? "Teacher" : "Student");

  const learningMethod = secondaryMarker === "ai"
    ? "an AI assistant"
    : secondaryMarker === "ai-fixed"
    ? "a guided AI assistant"
    : secondaryMarker === "web"
    ? "web search"
    : "reading materials";

  // Select appropriate questions based on role
  const questions = isLearner ? LEARNER_QUESTIONS : (isTeacher ? TEACHER_QUESTIONS : STUDENT_QUESTIONS);

  // Role colors
  const roleColor = isLearner ? "violet" : (isTeacher ? "blue" : "emerald");
  const gradientClass = isLearner
    ? "bg-gradient-to-r from-violet-600 to-purple-600"
    : isTeacher
    ? "bg-gradient-to-r from-blue-600 to-indigo-600"
    : "bg-gradient-to-r from-emerald-600 to-teal-600";
  const buttonClass = isLearner
    ? "bg-violet-600 hover:bg-violet-700"
    : isTeacher
    ? "bg-blue-600 hover:bg-blue-700"
    : "bg-emerald-600 hover:bg-emerald-700";
  const selectedClass = isLearner
    ? "bg-violet-600 text-white"
    : isTeacher
    ? "bg-blue-600 text-white"
    : "bg-emerald-600 text-white";

  // Screen state: 1 = Role/Topic/Examples, 2 = Trait Self-Assessment, 3 = State Confidence
  const [screen, setScreen] = useState(1);

  // State for trait self-assessment answers (explaining/learning ability)
  const [answers, setAnswers] = useState({});

  // State for baseline state confidence (3-item scale)
  const [stateConfidence, setStateConfidence] = useState(getEmptyConfidenceState());

  const handleAnswerChange = (questionId, value) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: value
    }));
  };

  const allTraitAnswered = questions.every(q => answers[q.id]);
  const allStateAnswered = isConfidenceComplete(stateConfidence);

  const handleContinue = () => {
    // Students skip Screen 2 - they answer self-assessment in ExampleQuestions
    if (isStudent) {
      next();
    } else {
      setScreen(2);
    }
  };

  const handleContinueToStateConfidence = () => {
    // Save trait assessment before moving to next phase
    const assessmentType = isLearner ? "selflearning_confidence" : (isTeacher ? "explaining_confidence" : "learning_confidence");
    player.set(`baseline_${assessmentType}`, answers);
    player.set("baseline_assessment_timestamp", new Date().toISOString());

    // Skip state confidence for ALL modes - confidence is measured AFTER learning
    // - Single mode (Learner): answers in RoleInstructions (Learning Consolidation)
    // - Dyad mode (Teacher): answers in RoleInstructions (Pre-Chat Preparation)
    // - Dyad mode (Student): answers in PostChatReflection (after learning from Teacher)
    next();
  };

  const handleStateConfidenceChange = (key, value) => {
    setStateConfidence(prev => ({ ...prev, [key]: value }));
  };

  const handleSubmit = () => {
    // Store the baseline state confidence (3-item scale)
    player.set("baseline_state_confidence", {
      ...stateConfidence,
      topicId: config?.id,
      timestamp: new Date().toISOString()
    });
    player.set("baseline_assessment_timestamp", new Date().toISOString());
    next();
  };

  // Helper to check if text contains HTML
  const containsHtml = (text) => /<[^>]+>/.test(text);

  // ============================================
  // SCREEN 1: Role + Topic + Example Questions
  // ============================================
  if (screen === 1) {
    return (
      <div className="h-full overflow-y-auto bg-gray-50 py-6 px-4">
        <div className="max-w-3xl mx-auto space-y-6">

          {/* Role Header */}
          <div className={`rounded-xl shadow-lg overflow-hidden ${gradientClass}`}>
            <div className="p-6 text-white">
              <p className="text-sm uppercase tracking-wide opacity-80">Your Role</p>
              <h1 className="text-3xl font-bold">{roleName}</h1>
            </div>
          </div>

          {/* Topic Reveal */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="text-center">
              {isStudent ? (
                <>
                  <p className="text-sm uppercase tracking-wide text-gray-500 mb-2">
                    Your Task
                  </p>
                  <h2 className="text-3xl font-bold text-gray-900 mb-4">Learn from Your Partner</h2>

                  <div className="text-left bg-gray-50 rounded-lg p-4 mt-4">
                    <p className="text-gray-600">
                      Your partner is currently learning a topic. Once they finish, they will teach you about it through a text chat conversation.
                    </p>
                    <p className="text-gray-600 mt-3">
                      While you wait, you will be assigned an <strong className="text-emerald-600">alternative reading task</strong> to keep you engaged.
                    </p>
                    <span className="block mt-3 font-medium text-gray-700">
                      You will be tested on the topic your partner teaches you.
                    </span>
                  </div>
                </>
              ) : (
                <>
                  <p className="text-sm uppercase tracking-wide text-gray-500 mb-2">
                    {isLearner ? "Your Topic to Learn" : "Your Topic to Learn & Teach"}
                  </p>
                  <h2 className="text-3xl font-bold text-gray-900 mb-4">{topicName}</h2>

                  <div className="text-left bg-gray-50 rounded-lg p-4 mt-4">
                    {isLearner ? (
                      <p className="text-gray-600">
                        You will learn about this topic using <strong className="text-violet-600">{learningMethod}</strong>.
                        <span className="block mt-2 font-medium text-gray-700">
                          You will be tested on this topic at the end.
                        </span>
                      </p>
                    ) : (
                      <p className="text-gray-600">
                        You will be given this topic using <strong className="text-blue-600">{learningMethod}</strong>,
                        along with a list of subtopics you need to cover. Study this material and prepare to
                        explain the topic to your partner (the Student) through a text chat.
                        <span className="block mt-2 text-gray-700">
                          The Student will not see any of these materials, so they can only learn what you explain to them.
                        </span>
                        <span className="block mt-2 font-medium text-gray-700">
                          Both of you will be tested on this topic at the end.
                        </span>
                      </p>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>

          {/* What to Expect - Shown when no example questions displayed */}
          {!showExamples && (
            <div className="bg-blue-50 border-2 border-blue-200 rounded-xl shadow-lg overflow-hidden">
              <div className="bg-blue-100 px-6 py-4">
                <h3 className="text-lg font-bold text-blue-900">What to Expect</h3>
              </div>

              <div className="p-6">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-full bg-blue-500 text-white flex items-center justify-center flex-shrink-0">
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25" />
                    </svg>
                  </div>
                  <div>
                    <p className="text-gray-700 text-base leading-relaxed">
                      You will learn about this topic and then be quizzed on it.
                      <strong className="text-blue-800"> Example questions will be shown</strong> during your learning phase to help guide your focus.
                    </p>
                    <p className="text-gray-600 text-sm mt-3">
                      Pay attention to these questions as they indicate what you'll need to understand.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* EXAMPLE Questions - Optional, controlled by showExamples prop (now disabled, shown in LearningPhase instead) */}
          {showExamples && exampleQuestions.length > 0 && (
            <div className="bg-amber-50 border-2 border-amber-300 rounded-xl shadow-lg overflow-hidden">
              {/* Example Header */}
              <div className="bg-amber-400 px-6 py-4">
                <h3 className="text-lg font-bold text-amber-900">EXAMPLE Questions</h3>
                <p className="text-sm text-amber-800">These are samples of what you'll be tested on</p>
              </div>

              {/* Example Questions List - Vertical Stack */}
              <div className="p-6">
                <p className="text-amber-800 text-sm font-medium mb-4">
                  Pay attention to these types of questions while you learn:
                </p>

                <div className="space-y-4">
                  {exampleQuestions.map((q, idx) => {
                    const questionHasHtml = containsHtml(q.text);

                    // Get bloom level info (config uses "Remembering", "Understanding", "Applying")
                    const bloomInfo = {
                      remembering: { label: "REMEMBER", desc: "Recall facts and basic concepts", color: "bg-blue-100 text-blue-800 border-blue-300" },
                      understanding: { label: "UNDERSTAND", desc: "Explain ideas or concepts", color: "bg-green-100 text-green-800 border-green-300" },
                      applying: { label: "APPLY", desc: "Use information in new situations", color: "bg-purple-100 text-purple-800 border-purple-300" },
                      analyzing: { label: "ANALYZE", desc: "Draw connections among ideas", color: "bg-orange-100 text-orange-800 border-orange-300" },
                      evaluating: { label: "EVALUATE", desc: "Justify a decision or action", color: "bg-red-100 text-red-800 border-red-300" },
                      creating: { label: "CREATE", desc: "Produce new or original work", color: "bg-pink-100 text-pink-800 border-pink-300" }
                    };
                    const bloom = bloomInfo[q.bloomLevel?.toLowerCase()] || bloomInfo.remembering;

                    return (
                      <div key={q.id} className={`bg-white border-2 ${bloom.color.split(' ')[2]} rounded-lg overflow-hidden`}>
                        {/* Horizontal layout: Bloom tag on left, question on right */}
                        <div className="flex items-start">
                          {/* Bloom Level Tag */}
                          <div className={`px-4 py-3 ${bloom.color.split(' ').slice(0,2).join(' ')} flex flex-col items-center justify-center min-w-[140px] text-center`}>
                            <span className="font-bold text-sm">{bloom.label}</span>
                            <span className="text-xs opacity-75 mt-1">{bloom.desc}</span>
                          </div>

                          {/* Question Content */}
                          <div className="p-4 flex-1">
                            <p className="text-xs text-gray-400 mb-1">Example {idx + 1}:</p>
                            {questionHasHtml ? (
                              <p
                                className="text-gray-800"
                                dangerouslySetInnerHTML={{ __html: q.text }}
                              />
                            ) : (
                              <p className="text-gray-800">{q.text}</p>
                            )}

                            {/* Options Preview */}
                            {q.options && q.options.length > 0 && (
                              <div className="mt-3 flex flex-wrap gap-2">
                                {q.options.map((option, optIndex) => (
                                  <span
                                    key={optIndex}
                                    className="inline-block px-3 py-1 bg-gray-100 text-gray-600 text-sm rounded-full"
                                  >
                                    {option.label}
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div className="bg-amber-100 rounded-lg p-3 mt-4">
                  <p className="text-amber-800 text-sm">
                    <strong>Note:</strong> The actual quiz will have more questions like these.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Continue Button */}
          <div className="mt-6">
            <button
              onClick={handleContinue}
              className={`w-full py-3 px-6 rounded-lg font-semibold text-white transition-all duration-200 ${buttonClass}`}
            >
              Continue
            </button>
          </div>

        </div>
      </div>
    );
  }

  // ============================================
  // SCREEN 2: Trait Self-Assessment
  // ============================================
  if (screen === 2) {
  return (
    <div className="h-full overflow-y-auto bg-gray-50 py-6 px-4">
      <div className="max-w-3xl mx-auto space-y-6">

        {/* Header reminder of role */}
        <div className={`rounded-xl shadow-lg overflow-hidden ${gradientClass}`}>
          <div className="p-4 text-white flex items-center justify-between">
            <div>
              <p className="text-sm opacity-80">Your Role</p>
              <p className="text-xl font-bold">{roleName}</p>
            </div>
            {!isStudent && (
              <div className="text-right">
                <p className="text-sm opacity-80">Topic</p>
                <p className="text-lg font-semibold">{topicName}</p>
              </div>
            )}
          </div>
        </div>

        {/* Confidence Self-Assessment */}
        <div className="bg-white rounded-xl shadow-lg p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-2">
            Quick Self-Assessment
          </h3>
          <p className="text-sm text-gray-500 mb-4">
            How confident are you in your ability to... (0 = Not at all, 6 = Very confident)
          </p>

          <div className="space-y-4">
            {questions.map((q, index) => (
              <div key={q.id} className="p-4 bg-gray-50 rounded-lg">
                <p className="text-gray-700 mb-3">
                  {index + 1}. {q.question}
                </p>
                <div className="flex justify-between items-center gap-2">
                  {LIKERT_OPTIONS.map(option => (
                    <label
                      key={option.value}
                      className={`flex-1 text-center py-2 px-1 rounded-lg cursor-pointer transition-all ${
                        answers[q.id] === option.value
                          ? selectedClass
                          : 'bg-white border border-gray-300 hover:border-gray-400'
                      }`}
                    >
                      <input
                        type="radio"
                        name={q.id}
                        value={option.value}
                        checked={answers[q.id] === option.value}
                        onChange={(e) => handleAnswerChange(q.id, e.target.value)}
                        className="sr-only"
                      />
                      {option.label}
                    </label>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* Continue Button */}
          <div className="mt-6">
            <button
              onClick={handleContinueToStateConfidence}
              disabled={!allTraitAnswered}
              className={`w-full py-3 px-6 rounded-lg font-semibold text-white transition-all duration-200 ${
                allTraitAnswered ? buttonClass : 'bg-gray-400 cursor-not-allowed'
              }`}
            >
              {allTraitAnswered ? "Continue" : "Please answer all questions"}
            </button>
          </div>
        </div>

      </div>
    </div>
  );
  }

  // ============================================
  // SCREEN 3: Baseline State Confidence
  // ============================================
  return (
    <div className="h-full overflow-y-auto bg-gray-50 py-6 px-4">
      <div className="max-w-3xl mx-auto space-y-6">

        {/* Header reminder of role */}
        <div className={`rounded-xl shadow-lg overflow-hidden ${gradientClass}`}>
          <div className="p-4 text-white flex items-center justify-between">
            <div>
              <p className="text-sm opacity-80">Your Role</p>
              <p className="text-xl font-bold">{roleName}</p>
            </div>
            <div className="text-right">
              <p className="text-sm opacity-80">Topic</p>
              <p className="text-lg font-semibold">{topicName}</p>
            </div>
          </div>
        </div>

        {/* State Confidence Assessment */}
        <div className="bg-white rounded-xl shadow-lg p-6">
          <ConfidenceScale
            title="Current Topic Confidence"
            instructions={`Before you begin, please rate your current confidence about ${topicName}:`}
            values={stateConfidence}
            onChange={handleStateConfidenceChange}
            color={roleColor}
            topicName={topicName}
          />

          {/* Note about baseline */}
          <div className="mt-4 p-3 bg-gray-100 rounded-lg">
            <p className="text-sm text-gray-600">
              <strong>Note:</strong> This measures your confidence <em>before</em> any learning{!isSingleMode && " or teaching"}.
              You'll answer similar questions later to track how your confidence changes.
            </p>
          </div>

          {/* Submit Button */}
          <div className="mt-6">
            <button
              onClick={handleSubmit}
              disabled={!allStateAnswered}
              className={`w-full py-3 px-6 rounded-lg font-semibold text-white transition-all duration-200 ${
                allStateAnswered ? buttonClass : 'bg-gray-400 cursor-not-allowed'
              }`}
            >
              {allStateAnswered ? "Continue" : "Please answer all questions"}
            </button>
          </div>
        </div>

      </div>
    </div>
  );
}
